import React, { FC, ReactNode } from "react";

import { Box, Paper, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

interface ShiftSettingsSectionProps {
	title: ReactNode;
	description?: ReactNode;
	action?: ReactNode;
	children: ReactNode;
	icon?: ReactNode;
	sx?: SxProps<Theme>;
	headerSx?: SxProps<Theme>;
	contentSx?: SxProps<Theme>;
}

export const ShiftSettingsSection: FC<ShiftSettingsSectionProps> = ({
	title,
	description,
	action,
	children,
	icon,
	sx,
	headerSx,
	contentSx,
}) => (
	<Paper
		elevation={0}
		sx={[
			{
				p: 2.5,
				borderRadius: 2,
				border: (theme) =>
					`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
				backgroundColor: "background.paper",
				transition: "box-shadow 0.2s ease, border-color 0.2s ease",
				"&:hover": {
					boxShadow: (theme) =>
						theme.palette.mode === "dark"
							? "0 4px 20px rgba(0,0,0,0.3)"
							: "0 4px 20px rgba(0,0,0,0.06)",
				},
			},
			sx,
		]}
	>
		<Box
			sx={[
				{
					display: "flex",
					alignItems: description ? "flex-start" : "center",
					justifyContent: "space-between",
					mb: 2,
					gap: 2,
				},
				headerSx,
			]}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
				{icon && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 32,
							height: 32,
							borderRadius: 1.25,
							bgcolor: (theme) =>
								theme.palette.mode === "dark"
									? "rgba(31, 128, 255, 0.15)"
									: "rgba(31, 128, 255, 0.08)",
							color: "primary.main",
						}}
					>
						{icon}
					</Box>
				)}
				<Box>
					<Typography
						variant="subtitle1"
						component="div"
						sx={{
							fontWeight: 600,
							lineHeight: 1.25,
							color: "text.primary",
						}}
					>
						{title}
					</Typography>
					{description && (
						<Typography
							variant="body2"
							sx={{ mt: 0.25, color: "text.secondary" }}
						>
							{description}
						</Typography>
					)}
				</Box>
			</Box>
			{action}
		</Box>
		<Box sx={contentSx}>{children}</Box>
	</Paper>
);
